<template>
  <div
    class="slot-layout"
    :class="{ [MAX_WIDTH_ALIGNED_CLASS]: isMaxWidthAligned(layoutStyle) }"
    :style="layoutStyle"
  >
    <template v-for="(item, index) in slotItems" :key="item.id">
      <RenderSlot :name="item.id" :children="slotChildren?.[item.id]" :editable="editable">
        <div class="slot-layout__empty">Section {{ index + 1 }}</div>
      </RenderSlot>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ComponentNode } from '../../../../api/types.gen'
import type { ColumnsOrRowsItemConfig } from './types'
import {
  getSizeStyleData,
  getSpacingStyleData,
  getMaxWidthMarginStyleData,
  getColorStyleData,
} from '../../../composables/useStyleData'
import { MAX_WIDTH_ALIGNED_CLASS, isMaxWidthAligned } from '../../../lib/maxWidthAlign'
import RenderSlot from '../../../RenderSlot.vue'
import type { ColumnsOrRowsFields as SlotLayoutFields } from './definition'

const props = withDefaults(
  defineProps<{
    fields?: SlotLayoutFields
    slotChildren?: Record<string, ComponentNode[]>
    editable?: boolean
  }>(),
  {
    fields: () => ({}),
  },
)

const slotItems = computed<ColumnsOrRowsItemConfig[]>(() => props.fields?.slots?.items ?? [])

const layoutStyle = computed(() => {
  const f = props.fields
  const dir = f?.direction || 'row'
  const items = slotItems.value
  const sizes = items.map((c) => getSizeStyleData(c.size, '1fr')).join(' ')

  const s: Record<string, string> = { display: 'grid' }

  if (dir === 'row') {
    s.gridTemplateColumns = sizes || '1fr'
  } else {
    s.gridTemplateRows = sizes || '1fr'
  }

  const gap = getSizeStyleData(f?.gap)
  if (gap) s.gap = gap
  if (f?.alignItems) s.alignItems = f.alignItems
  if (f?.justifyContent) s.justifyContent = f.justifyContent

  const maxWidth = getSizeStyleData(f?.maxWidth)
  if (maxWidth) s.maxWidth = maxWidth
  const padding = getSpacingStyleData(f?.padding)
  if (padding) s.padding = padding
  // The authored margin, and a capped grid placed by its alignment, centred when unset
  // (render/lib/maxWidthAlign.ts).
  Object.assign(s, getMaxWidthMarginStyleData(f?.margin, maxWidth, f?.maxWidthAlign, 'center'))
  const bgColor = getColorStyleData(f?.bgColor)
  if (bgColor) s.backgroundColor = bgColor
  const borderWidth = getSizeStyleData(f?.borderWidth)
  if (borderWidth) s.borderWidth = borderWidth
  const borderColor = getColorStyleData(f?.borderColor)
  if (borderColor) s.borderColor = borderColor
  if (borderWidth || borderColor) s.borderStyle = 'solid'
  const borderRadius = getSizeStyleData(f?.borderRadius)
  if (borderRadius) s.borderRadius = borderRadius

  return s
})
</script>

<style scoped>
.slot-layout {
  box-sizing: border-box;
}

.slot-layout__empty {
  min-height: 32px;
  border: 1.5px dashed #ccc;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 0.7rem;
  padding: 4px 8px;
  opacity: 0.7;
  user-select: none;
}
</style>
